import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { utils, ModalPane } from '@costar/theme-ui';
import { useToast } from '@costar/theme-ui-toast';
import { useMountedState } from 'react-use';
import TransferList from '../../../transfer-list/transfer-list.esm.js';
import MTLContainer from '../../../containers/modify-table-layout/mtl-container.esm.js';
import { createContainerTextSelector } from './selectors/container-text-selector.esm.js';
import { createAvailableOptionsSelector } from './selectors/available-options-selector.esm.js';
import { createSelectedOptionsSelector } from './selectors/selected-options-selector.esm.js';
import { TooltipContent } from '../../tooltip-content.esm.js';
import { createAvailableListHeader } from '../available-list-header/available-list-header.esm.js';
import { createFilterSelector } from './selectors/filter-selector.esm.js';
import { createDisplayLayoutTypeSelector } from '../selectors/display-layout-type-selector.esm.js';
import { createHideSelectListFooterSelector } from './selectors/hide-select-list-footer-selector.esm.js';
import { createApplyButtonEnabledSelector } from './selectors/apply-button-enabled-selector.esm.js';
import { createTransferListCallbackSelector } from './selectors/transfer-list-callback-selector.esm.js';
import { createSaveAsCallbackSelector } from './selectors/save-as-callback-selector.esm.js';
import { createDeleteCallbackSelector } from './selectors/delete-callback-selector.esm.js';
import { createSaveCallbackSelector } from './selectors/save-callback-selector.esm.js';
import { createResetCallbackSelector } from './selectors/reset-callback-selector.esm.js';
import { createApplyCallbackSelector } from './selectors/apply-callback-selector.esm.js';
import { createDisplayLayoutNameSelector } from '../selectors/display-layout-name-selector.esm.js';
import { createOtherLayoutNamesSelector } from '../selectors/other-layout-names-selector.esm.js';
import { createPredefinedLayoutNamesSelector } from '../selectors/predefined-layout-names-selector.esm.js';
import { createSomeFieldsSelectedSelector } from './selectors/some-fields-selected-selector.esm.js';
import { createSelectListHeader } from '../select-list-header/select-list-header.esm.js';
import { SelectListFooter } from '../select-list-footer/select-list-footer.esm.js';
import { SaveMsg } from '../save-message/save-message.esm.js';
import { LayoutType } from '../../types/layouts.esm.js';
import { keys } from '../../constants/keys.esm.js';
import { ResetDialog } from '../dialogs/reset-dialog/reset-dialog.esm.js';
import { NoFieldsSelectedWarning } from '../dialogs/no-fields-selected-warning/no-fields-selected-warning.esm.js';
import { DeleteDialog } from '../dialogs/delete-dialog/delete-dialog.esm.js';
import { SaveAsDialog } from '../dialogs/save-as-dialog/save-as-dialog.esm.js';

function noop() {
  return _noop.apply(this, arguments);
}
function _noop() {
  _noop = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return _noop.apply(this, arguments);
}
function createModifyTableLayout() {
  var displayLayoutTypeSelector = createDisplayLayoutTypeSelector();
  var applyButtonEnabledSelector = createApplyButtonEnabledSelector();
  var hideSelectListFooterSelector = createHideSelectListFooterSelector();
  var saveCallbackSelector = createSaveCallbackSelector();
  var resetCallbackSelector = createResetCallbackSelector();
  var deleteCallbackSelector = createDeleteCallbackSelector();
  var saveAsCallbackSelector = createSaveAsCallbackSelector();
  var applyCallbackSelector = createApplyCallbackSelector();
  var availableOptionsSelector = createAvailableOptionsSelector();
  var transferListCallbackSelector = createTransferListCallbackSelector();
  var filterSelector = createFilterSelector();
  var selectedOptionsSelector = createSelectedOptionsSelector();
  var someFieldsSelectedSelector = createSomeFieldsSelectedSelector();
  var displayLayoutNameSelector = createDisplayLayoutNameSelector();
  var otherLayoutNamesSelector = createOtherLayoutNamesSelector();
  var predefinedLayoutNamesSelector = createPredefinedLayoutNamesSelector();
  var containerTextSelector = createContainerTextSelector();
  var AvailableListHeader = createAvailableListHeader();
  var SelectListHeader = createSelectListHeader();
  function ModifyTableLayout(props) {
    var isMounted = useMountedState();
    var layouts = props.layouts,
      onChange = props.onChange,
      onCloseProp = props.onClose,
      _props$fontSize = props.fontSize,
      fontSize = _props$fontSize === void 0 ? 'sm' : _props$fontSize,
      _props$onDeleteLayout = props.onDeleteLayout,
      onDeleteLayout = _props$onDeleteLayout === void 0 ? noop : _props$onDeleteLayout,
      _props$onSaveLayout = props.onSaveLayout,
      onSaveLayout = _props$onSaveLayout === void 0 ? noop : _props$onSaveLayout,
      resources = props.resources,
      allFields = props.allFields,
      isOpen = props.isOpen,
      categories = props.categories;
    var _useState = useState(layouts),
      _useState2 = _slicedToArray(_useState, 2),
      displayLayouts = _useState2[0],
      setDisplayLayouts = _useState2[1];
    var defaultLayout = useMemo(function () {
      return layouts.find(function (l) {
        return l.type === LayoutType.Default;
      });
    }, [layouts]);
    //Detect changes to the default layout: this can happen when property type filter is changed in costar-suite.
    useEffect(function () {
      setDisplayLayouts(function (previous) {
        var _previous$find;
        if (!defaultLayout) {
          //Should not happen. There should always be a default layout.
          return previous;
        }
        //Remove the previous default layout from the state. And replace it.
        var defaultSelected = ((_previous$find = previous.find(function (l) {
          return l.type === LayoutType.Default;
        })) === null || _previous$find === void 0 ? void 0 : _previous$find.selected) || false;
        var other = previous.filter(function (l) {
          return l.type !== LayoutType.Default;
        });
        return [_objectSpread2(_objectSpread2({}, defaultLayout), {}, {
          selected: defaultSelected
        })].concat(_toConsumableArray(other));
      });
    }, [defaultLayout]);
    var _useState3 = useState(keys.allFields),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedCategory = _useState4[0],
      onCategoryChange = _useState4[1];
    var noFieldsSelectedWarningRef = useRef(null);
    var resetRef = useRef(null);
    var transferListRef = useRef(null);
    var deleteDialogRef = useRef(null);
    var saveAsDialogRef = useRef(null);
    var primaryTitleId = utils.useUniqueId();
    var primaryDescriptionId = utils.useUniqueId();
    var _useToast = useToast(),
      _useToast2 = _slicedToArray(_useToast, 1),
      toast = _useToast2[0];
    var layoutType = displayLayoutTypeSelector({
      displayLayouts: displayLayouts
    });
    var fieldsSelected = someFieldsSelectedSelector({
      displayLayouts: displayLayouts
    });
    var applyButtonEnabled = applyButtonEnabledSelector({
      displayLayouts: displayLayouts
    });
    var actionProps = {
      layouts: layouts,
      onChange: onChange,
      displayLayouts: displayLayouts,
      setDisplayLayouts: setDisplayLayouts,
      onDeleteLayout: onDeleteLayout,
      onSaveLayout: onSaveLayout
    };
    var hideSelectListFooterComponent = hideSelectListFooterSelector(actionProps);
    var onSave = saveCallbackSelector(actionProps);
    var onReset = resetCallbackSelector(actionProps);
    var onDelete = deleteCallbackSelector(actionProps);
    var onSaveAs = saveAsCallbackSelector(actionProps);
    var onApply = applyCallbackSelector(actionProps);
    var onSelectionChange = transferListCallbackSelector(_objectSpread2(_objectSpread2({}, props), {}, {
      displayLayouts: displayLayouts,
      setDisplayLayouts: setDisplayLayouts
    }));

    // Callbacks
    var onResetClick = useCallback(function () {
      var _resetRef$current;
      return (_resetRef$current = resetRef.current) === null || _resetRef$current === void 0 ? void 0 : _resetRef$current.open();
    }, []);
    var onDeleteClick = useCallback(function () {
      var _deleteDialogRef$curr;
      return (_deleteDialogRef$curr = deleteDialogRef.current) === null || _deleteDialogRef$curr === void 0 ? void 0 : _deleteDialogRef$curr.open();
    }, []);
    var onSaveAsClick = useCallback(function () {
      var _saveAsDialogRef$curr;
      return (_saveAsDialogRef$curr = saveAsDialogRef.current) === null || _saveAsDialogRef$curr === void 0 ? void 0 : _saveAsDialogRef$curr.open();
    }, []);
    var saveClick = useCallback(function () {
      if (onSave) {
        onSave();
      }
      toast({
        position: 'top',
        duration: 2000,
        render: function render() {
          return /*#__PURE__*/React.createElement(SaveMsg, {
            message: resources.saveConfirmation()
          });
        }
      });
    }, [onSave, resources, toast]);
    var applyLayout = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
      var _noFieldsSelectedWarn;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!applyButtonEnabled) {
              _context.next = 7;
              break;
            }
            if (!onApply) {
              _context.next = 4;
              break;
            }
            _context.next = 4;
            return onApply();
          case 4:
            if (onCloseProp && isMounted()) {
              onCloseProp();
            }
            _context.next = 8;
            break;
          case 7:
            if (isMounted()) {
              (_noFieldsSelectedWarn = noFieldsSelectedWarningRef.current) === null || _noFieldsSelectedWarn === void 0 ? void 0 : _noFieldsSelectedWarn.open();
            }
          case 8:
          case "end":
            return _context.stop();
        }
      }, _callee);
    })), [applyButtonEnabled, isMounted, onApply, onCloseProp]);
    var onCloseCb = useCallback(function () {
      setDisplayLayouts(layouts);
      if (onCloseProp) {
        onCloseProp();
      }
    }, [layouts, onCloseProp]);
    var selectInfoTooltipContent = useMemo(function () {
      return /*#__PURE__*/React.createElement(TooltipContent, {
        resources: resources.tooltip
      });
    }, [resources]);
    var availableListHeaderComponent = useMemo(function () {
      return categories !== null && categories !== void 0 && categories.length ? /*#__PURE__*/React.createElement(AvailableListHeader, {
        resources: resources,
        categories: categories,
        containerElement: transferListRef.current,
        selectedCategory: selectedCategory,
        onCategoryChange: onCategoryChange
      }) : null;
    }, [categories, resources, selectedCategory]);
    var selectListHeaderComponent = useMemo(function () {
      return /*#__PURE__*/React.createElement(SelectListHeader, {
        displayLayouts: displayLayouts,
        layouts: layouts,
        setDisplayLayouts: setDisplayLayouts,
        allFields: allFields,
        resources: resources,
        containerElement: transferListRef.current
      });
    }, [allFields, displayLayouts, layouts, resources]);
    var selectListFooterComponent = useMemo(function () {
      return /*#__PURE__*/React.createElement(SelectListFooter, {
        layoutType: layoutType,
        fieldsSelected: fieldsSelected,
        resources: resources,
        onDeleteClick: onDeleteClick,
        onSaveAsClick: onSaveAsClick,
        onSaveClick: saveClick
      });
    }, [fieldsSelected, layoutType, onDeleteClick, onSaveAsClick, resources, saveClick]);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ResetDialog, {
      ref: resetRef,
      resources: resources.resetDialog,
      onConfirm: onReset,
      fontSize: fontSize
    }), /*#__PURE__*/React.createElement(ModalPane, {
      useModalAsBackdrop: true,
      disableScrollLock: true,
      disableBackdropClick: true,
      open: isOpen,
      onClose: onCloseCb,
      "aria-labelledby": primaryTitleId,
      "aria-describedby": primaryDescriptionId,
      variant: "modal.cosmos",
      maxWidth: "701px" // 701px will allow the dropdown to have an integer pixel width which will result in a clean drop down render
      ,
      minWidth: "540px",
      minHeight: "652px",
      maxHeight: "820px",
      height: "100%",
      fontSize: fontSize
    }, /*#__PURE__*/React.createElement(MTLContainer, {
      labels: containerTextSelector(props),
      a11yTitleId: primaryTitleId,
      a11yDescriptionId: primaryDescriptionId,
      onResetClick: onResetClick,
      onApplyClick: applyLayout,
      onCloseClick: onCloseCb,
      secondaryModal: null
    }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(NoFieldsSelectedWarning, {
      ref: noFieldsSelectedWarningRef,
      resources: resources.noFieldsSelected,
      fontSize: fontSize
    }), /*#__PURE__*/React.createElement(DeleteDialog, {
      ref: deleteDialogRef,
      layoutName: displayLayoutNameSelector({
        displayLayouts: displayLayouts
      }),
      onConfirm: onDelete,
      resources: resources.deleteDialog,
      fontSize: fontSize
    }), /*#__PURE__*/React.createElement(SaveAsDialog, {
      ref: saveAsDialogRef,
      onConfirm: onSaveAs,
      saveAsResources: resources.saveAsDialog,
      confirmOverrideResources: resources.confirmOverrideDialog,
      layoutName: displayLayoutNameSelector({
        displayLayouts: displayLayouts
      }),
      otherLayoutNames: otherLayoutNamesSelector({
        displayLayouts: displayLayouts
      }),
      predefinedLayoutNames: predefinedLayoutNamesSelector(displayLayouts),
      fontSize: fontSize
    }), /*#__PURE__*/React.createElement(TransferList, {
      ref: transferListRef,
      availableOptions: availableOptionsSelector(props),
      filter: filterSelector(_objectSpread2(_objectSpread2({}, props), {}, {
        selectedCategory: selectedCategory
      })),
      selectedOptions: selectedOptionsSelector({
        displayLayouts: displayLayouts
      }),
      availableOptionsLabel: resources.availableOptionsLabel(),
      selectInfoTooltipContent: selectInfoTooltipContent,
      selectedOptionsLabel: resources.selectedOptionsLabel(),
      sortMode: "alphabetical",
      hasLockSelections: true,
      fontSize: "sm",
      buttonSize: "sm",
      onChange: onSelectionChange,
      isAvailableOptionsSearchable: true,
      isSearchClearable: true,
      ignoreOnChangeUntilMounted: true,
      availableSearchPlaceholder: resources.availableSearchPlaceholder(),
      availableListHeaderComponent: availableListHeaderComponent,
      selectListHeaderComponent: selectListHeaderComponent,
      hideSelectListFooterComponent: hideSelectListFooterComponent,
      selectListFooterComponent: selectListFooterComponent
    })))));
  }
  ModifyTableLayout.displayName = 'ModifyTableLayout';
  return ModifyTableLayout;
}

export { createModifyTableLayout };
//# sourceMappingURL=modify-table-layout.esm.js.map
